<script setup>
import { ref, watch } from 'vue'
import { request } from '../api/request'
import { getToken } from '../api/session'
const props = defineProps({ venueId: { type: [String, Number], required: true }, gameId: { type: Number, default: 0 } })
const emit = defineEmits(['login'])
const games = ref([]),
  venue = ref(null),
  loading = ref(false),
  error = ref(''),
  page = ref(1),
  total = ref(0),
  launching = ref(false)
let version = 0
async function load() {
  const current = ++version,
    token = getToken()
  games.value = []
  venue.value = null
  error.value = ''
  if (!token) return
  loading.value = true
  try {
    const [venues, response] = await Promise.all([
      request('/game/venue/list'),
      request('/game/page', {
        method: 'POST',
        data: { venueId: Number(props.venueId), pageNumber: page.value, pageSize: 20 },
      }),
    ])
    if (current !== version || token !== getToken()) return
    venue.value = (venues || []).find((item) => String(item.id) === String(props.venueId))
    games.value = response?.data || []
    total.value = Number(response?.total || 0)
  } catch (e) {
    if (current === version) error.value = e.message
  } finally {
    if (current === version) loading.value = false
  }
}
async function launch(game) {
  if (!getToken()) {
    emit('login')
    return
  }
  if (launching.value) return
  launching.value = true
  error.value = ''
  try {
    const data = await request(`/game/venue/${props.venueId}/launch`, {
      method: 'POST',
      data: { id: Number(props.venueId), gameId: Number(game?.id || 0) },
    })
    const target = new URL(data?.launchUrl || data?.url || '')
    if (!['http:', 'https:'].includes(target.protocol)) throw new Error('场馆地址无效')
    window.location.assign(target.href)
  } catch (e) {
    error.value = e.message
  } finally {
    launching.value = false
  }
}
watch(
  () => [props.venueId, getToken()],
  () => {
    page.value = 1
    load()
  },
  { immediate: true },
)
watch(page, load)
</script>
<template>
  <section class="container advertising-venue">
    <h2>{{ venue?.name || '场馆游戏' }}</h2>
    <button v-if="!getToken()" class="primary" @click="emit('login')">登录查看场馆</button>
    <p v-else-if="loading">加载中…</p>
    <div v-else-if="error" role="alert">{{ error }} <button @click="load">重试</button></div>
    <template v-else>
      <button v-if="Number.isSafeInteger(gameId) && gameId > 0" class="primary" :disabled="launching" @click="launch({ id: gameId })">进入推荐游戏</button>
      <div class="advertising-venue-games">
        <button v-for="game in games" :key="game.id" :disabled="launching" @click="launch(game)">
          <img
            v-if="game.imageUrl || game.iconUrl"
            :src="game.imageUrl || game.iconUrl"
            :alt="game.name"
          /><span>{{ game.name }}</span>
        </button>
      </div>
      <p v-if="venue && !games.length">该场馆暂无可用游戏</p>
      <p v-if="!venue && !games.length">该场馆暂未开放</p>
      <div v-if="total > 20">
        <button :disabled="page <= 1" @click="page--">上一页</button><span>第 {{ page }} 页</span
        ><button :disabled="page * 20 >= total" @click="page++">下一页</button>
      </div>
    </template>
  </section>
</template>
<style scoped>
.advertising-venue {
  padding: 32px 0;
}
.advertising-venue-games {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 18px;
  margin: 20px 0;
}
.advertising-venue-games button {
  background: white;
  border: 1px solid #dce5f1;
  border-radius: 12px;
  overflow: hidden;
  padding: 12px;
}
.advertising-venue-games img {
  width: 100%;
  height: 120px;
  object-fit: contain;
}
.advertising-venue-games span {
  display: block;
  padding-top: 12px;
}
</style>
